/**
 * RiskGeometry — along-path slicing of a Google route path by metre offsets. The backend expresses
 * segments, mandatory stops and hotspots as route-absolute km values, so the overlay has to map those
 * back onto the detailed route path that was sent to the engine (the concatenated step paths; see
 * RiskAssessmentController._detailedPathOf). Builds on the cumulative-distance helper from
 * SafetyRouting/GeoUtils.
 *
 * All functions are pure; `cum` is `cumulativeDistances(path)` computed once per route by the caller.
 */

/* eslint-disable no-undef */

export { cumulativeDistances } from "../SafetyRouting/GeoUtils";

/**
 * Index of the first vertex whose cumulative distance is >= `m` (binary search). Returns
 * `cum.length - 1` when `m` is beyond the end.
 *
 * @param {number[]} cum
 * @param {number} m
 * @returns {number}
 */
function upperIndex(cum, m) {
  let lo = 0;
  let hi = cum.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] < m) {
      lo = mid + 1;
    } else {
      hi = mid;
    }
  }
  return lo;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Point on the path `m` metres from its start (clamped to [0, length]).
 *
 * @param {google.maps.LatLng[]} path
 * @param {number[]} cum
 * @param {number} m
 * @returns {google.maps.LatLng|null}
 */
export function pointAtMetres(path, cum, m) {
  if (!path || !path.length) {
    return null;
  }
  if (path.length === 1) {
    return path[0];
  }
  const total = cum[cum.length - 1];
  const target = clamp(Number(m) || 0, 0, total);
  const i = upperIndex(cum, target);
  if (i === 0) {
    return path[0];
  }
  const a = path[i - 1];
  const b = path[i];
  const span = cum[i] - cum[i - 1];
  if (span <= 0) {
    return b;
  }
  return google.maps.geometry.spherical.interpolate(a, b, (target - cum[i - 1]) / span);
}

/**
 * The sub-path between `fromM` and `toM` metres along the path, with interpolated end points so the
 * slice starts and ends exactly at the requested offsets. Both offsets are clamped to the path length;
 * an empty or inverted range yields a two-point slice at the clamped position.
 *
 * @param {google.maps.LatLng[]} path
 * @param {number[]} cum
 * @param {number} fromM
 * @param {number} toM
 * @returns {google.maps.LatLng[]}
 */
export function slicePathByMetres(path, cum, fromM, toM) {
  if (!path || !path.length) {
    return [];
  }
  if (path.length === 1) {
    return [path[0]];
  }
  const total = cum[cum.length - 1];
  const start = clamp(Math.min(Number(fromM) || 0, Number(toM) || 0), 0, total);
  const end = clamp(Math.max(Number(fromM) || 0, Number(toM) || 0), 0, total);

  const slice = [pointAtMetres(path, cum, start)];
  let i = upperIndex(cum, start);
  if (cum[i] <= start) {
    i += 1;
  }
  for (; i < path.length && cum[i] < end; i++) {
    slice.push(path[i]);
  }
  slice.push(pointAtMetres(path, cum, end));
  return slice;
}
